<template>
  <div>
    <v-dialog v-model="show">
      <v-card>
        <v-card-title>
          Details
          <v-spacer></v-spacer>
          <!-- Group by -->
          <v-select
            label="Group by"
            item-text="title"
            item-value="val"
            multiple
            :items="groupByItems"
            v-model="groupBy"
            clearable
            @click:clear="groupBy = null"
          >
          </v-select>
          <!-- /Group by -->
          <v-spacer></v-spacer>
          <!-- Search -->
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
          ></v-text-field>
          <!-- /Search -->
          <v-spacer></v-spacer>
          <v-btn icon @click.prevent="show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            dense
            fixed-header
            height="60vh"
            :key="'tabel-' + updateTable"
            :headers="headers"
            :items="list"
            :items-per-page="50"
            :group-by="groupBy"
            :search="search"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, 40, 50, -1]
            }"
          >
            <template v-slot:[`header.Real_Dur`]="{ header }">
              {{ header.text }}
              <br>Total: {{ calculateTotalOf(list, 'Real_Dur') }}
            </template>
            <template v-slot:[`header.Report_Dur`]="{ header }">
              {{ header.text }}
              <br>Total: {{ calculateTotalOf(list, 'Report_Dur') }}
            </template>
            <template v-slot:[`header.Invoice_Dur`]="{ header }">
              {{ header.text }}
              <br>Total: {{ calculateTotalOf(list, 'Invoice_Dur') }}
            </template>
            <!-- Start date -->
            <template v-slot:[`item.Start_Date`]="{ item }">
              {{ item.Start_Date }}
            </template>
            <!-- /Start date -->
            <!-- Reported duration -->
            <template v-slot:[`item.Real_Dur`]="{ item }">
              <v-menu
                v-model="reportedDurMenu[item.Reporting_ID]"
                :nudge-right="40"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
                :disabled="isClosedSummary"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    dense
                    v-model="item.Real_Dur"
                    prepend-icon="mdi-clock-time-four-outline mdi-18px"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="reportedDurMenu[item.Reporting_ID]"
                  v-model="item.Real_Dur"
                  format="24hr"
                  :allowed-minutes="allowedMinutes"
                  full-width
                  @click:minute="updateReport(item)"
                ></v-time-picker>
              </v-menu>
            </template>
            <!-- /Reported duration -->
            <!-- Paid duration -->
            <template v-slot:[`item.Report_Dur`]="{ item }">
              <v-menu
                v-model="paidDurMenu[item.Reporting_ID]"
                :nudge-right="40"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
                :disabled="isClosedSummary"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    dense
                    v-model="item.Report_Dur"
                    prepend-icon="mdi-clock-time-four-outline mdi-18px"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="paidDurMenu[item.Reporting_ID]"
                  v-model="item.Report_Dur"
                  format="24hr"
                  :allowed-minutes="allowedMinutes"
                  full-width
                  @click:minute="updateReport(item)"
                ></v-time-picker>
              </v-menu>
            </template>
            <!-- /Paid duration -->
            <!-- Invoiced duration -->
            <template v-slot:[`item.Invoice_Dur`]="{ item }">
              <v-menu
                v-model="invoicedDurMenu[item.Reporting_ID]"
                :nudge-right="40"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
                :disabled="isClosedSummary"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    dense
                    v-model="item.Invoice_Dur"
                    prepend-icon="mdi-clock-time-four-outline mdi-18px"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="invoicedDurMenu[item.Reporting_ID]"
                  v-model="item.Invoice_Dur"
                  format="24hr"
                  :allowed-minutes="allowedMinutes"
                  full-width
                  @click:minute="updateReport(item)"
                ></v-time-picker>
              </v-menu>
            </template>
            <!-- /Invoiced duration -->
            <!-- Activity type -->
            <template v-slot:[`item.Activity_Type`]="{ item }">
              <v-select
                dense
                v-model="item.Activity_Type"
                :items="activityTypes"
                item-text="Description"
                item-value="Activity_Type"
                @change="updateReport(item)"
                :readonly="isClosedSummary"
              ></v-select>
            </template>
            <!-- /Activity type -->
            <template v-slot:[`item.Description`]="{ item }">
              <span
                @click.prevent="!isClosedSummary && toggleDescriptionDialog(item)"
                style="cursor: pointer"
              >
                {{ item.Description }}
              </span>
            </template>
            <!-- Group header -->
            <template v-slot:[`group.header`]="{ items, isOpen, toggle }">
              <td :colspan="headers.length - 1" class="primary white--text">
                <div class="d-flex align-center">
                  <v-btn icon small @click="toggle">
                    <v-icon color="white">
                      {{ isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                  </v-btn>
                  <span class="subtitle-2 ml-2">{{ groupTitle(items[0]) }}</span>
                </div>
              </td>
            </template>
            <!-- /Group header -->
            <!-- Group summary -->
            <template v-slot:[`group.summary`]="{ items, headers }">
              <td
                v-for="(h, i) in headers"
                :key="h.value || h.text"
                class="primary darken-2 white--text"
              >
                {{ i === 0 ? 'Summary' : '' }}
                {{ h.value === 'Report_Dur' ? totalTime(items, h.value) : '' }}
                {{ h.value === 'Real_Dur' ? totalTime(items, h.value) : '' }}
                {{ h.value === 'Invoice_Dur' ? totalTime(items, h.value) : '' }}
              </td>
            </template>
            <!-- /Group summary -->
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Edit description -->
    <v-dialog
      v-if="currentItem"
      v-model="showTextAreaDialog"
      content-class="dialog-content"
      scrollable
      min-width="300"
      max-width="70vw"
    >
      <v-card>
        <div class="pa-2">
          <DescriptionWithSpellCheck
            :key="'textarea-' + showTextAreaDialog"
            label="Description"
            v-model="currentItem.Description"
          />
        </div>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn small outlined color="primary" class="ml-auto" @click.prevent="cancelDescription">
            Cancel
          </v-btn>
          <v-btn small color="primary" @click.prevent="updateReport(currentItem)">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- /Edit description -->
  </div>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'

export default {
  name: 'SummaryDetails',
  components: {
    DescriptionWithSpellCheck: () => import('@/components/reporting/DescriptionWithSpellCheck.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    },
    list: {
      required: false,
      type: Array,
      default: () => {
        return []
      }
    },
    isClosedSummary: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      updateTable: 0,
      groupBy: null,
      totalReported: 0,
      totalPaid: 0,
      totalInvoiced: 0,
      search: '',
      reportedDurMenu: {},
      paidDurMenu: {},
      invoicedDurMenu: {},
      currentItem: null,
      showTextAreaDialog: false,
      originalDescription: '',
      originalList: {},
      groupByItems: [
        {
          title: 'Project',
          val: 'ProjectName'
        },
        {
          title: 'Task',
          val: 'TaskName'
        },
        {
          title: 'Start date',
          val: 'Start_Date'
        },
        {
          title: 'Employee',
          val: 'Name'
        },
        {
          title: 'Activity',
          val: 'Activity_Type'
        }
      ],
      headers: [
        {
          text: 'Project name',
          value: 'ProjectName',
          divider: true,
          cellClass: 'text-no-wrap',
          class: 'text-no-wrap'
        },
        {
          text: 'Task name',
          value: 'TaskName',
          divider: true,
          class: 'text-no-wrap'
        },
        {
          text: 'Employee',
          value: 'Name',
          divider: true,
          class: 'text-no-wrap'
        },
        {
          text: 'Start date',
          value: 'Start_Date',
          divider: true,
          class: 'text-no-wrap'
        },
        {
          text: 'Reported',
          value: 'Real_Dur',
          divider: true,
          cellClass: 'text-no-wrap',
          class: 'text-no-wrap',
          width: 110
        },
        {
          text: 'Paid',
          value: 'Report_Dur',
          divider: true,
          cellClass: 'text-no-wrap',
          class: 'text-no-wrap',
          width: 110
        },
        {
          text: 'Invoiced',
          value: 'Invoice_Dur',
          divider: true,
          cellClass: 'text-no-wrap',
          class: 'text-no-wrap',
          width: 110
        },
        {
          text: 'Activity',
          value: 'Activity_Type',
          divider: true,
          cellClass: 'text-no-wrap',
          class: 'text-no-wrap',
          width: 150
        },
        {
          text: 'Description',
          value: 'Description'
        }
      ]
    }
  },
  computed: {
    activityTypes () {
      return this.$store.getters.getActivityTypes
    },
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    settings () {
      return this.$store.getters.getSettings
    },
    user () {
      return this.$store.getters.getUser
    },
    employeeRoles () {
      return this.$store.getters.getEmployeeRoles
    }
  },
  mounted () {
    if (this.list.length) {
      for (let i = 0; i < this.list.length; i++) {
        this.list[i].Start_Date = formatLocalDate(this.list[i].Start_Date)
        this.$set(this.reportedDurMenu, this.list[i].Reporting_ID, false)
        this.$set(this.paidDurMenu, this.list[i].Reporting_ID, false)
        this.$set(this.invoicedDurMenu, this.list[i].Reporting_ID, false)
        this.originalList[this.list[i].Reporting_ID] = Object.assign({}, this.list[i])
      }
    }
  },
  methods: {
    addTime (time1, time2) {
      const hours1 = +time1.split(':')[0]
      const hours2 = +time2.split(':')[0]
      const minutes1 = +time1.split(':')[1]
      const minutes2 = +time2.split(':')[1]
      const resOfMinutes = (minutes1 + minutes2) / 60 | 0
      const sumOfHours = resOfMinutes + hours1 + hours2
      const sumOfMinutes = (minutes1 + minutes2) - (60 * resOfMinutes)
      return ('' + sumOfHours).padStart(2, '0') + ':' + ('' + sumOfMinutes).padStart(2, '0')
    },
    totalTime (items, key) {
      let total = items[0][key]
      for (let i = 1; i < items.length; i++) {
        const time2 = items[i][key]
        total = this.addTime(total, time2)
      }
      return total
    },
    groupTitle (item) {
      let str = ''
      for (let i = 0; i < this.groupBy.length; i++) {
        str += item[this.groupBy[i]] + (i === this.groupBy.length - 1 ? '' : ', ')
      }
      return str
    },
    allowedMinutes (m) {
      return m === 30 || m === 0
    },
    updateReport (item) {
      if (this.canReport(item.ProjectName)) {
        this.reportedDurMenu[item.Reporting_ID] = false
        this.paidDurMenu[item.Reporting_ID] = false
        this.invoicedDurMenu[item.Reporting_ID] = false
        this.showTextAreaDialog = false
        this.axios.put('/api/repbydate/' + item.Reporting_ID, item, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(response => {
          this.$store.dispatch('setSuccess', { successMsg: 'Report successfully updated' })
        }).catch(error => {
          for (let i = 0; i < this.list.length; i++) {
            if (this.list[i].Reporting_ID === item.Reporting_ID) {
              this.list[i] = this.originalList[item.Reporting_ID]
              break
            }
          }
          this.updateTable++
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    cancelDescription () {
      this.showTextAreaDialog = !this.showTextAreaDialog
      this.currentItem.Description = this.originalDescription
      this.originalDescription = ''
    },
    toggleDescriptionDialog (item) {
      if (item) this.currentItem = item
      this.showTextAreaDialog = !this.showTextAreaDialog
      this.originalDescription = item.Description
    },
    calculateTotalOf (items, key) {
      return items.reduce((prevVal, item) => {
        return +prevVal.toFixed(2) + +this.timeToDecimal(item[key])
      }, 0).toFixed(2)
    },
    timeToDecimal (item) {
      var hoursMinutes = item.split(/[.:]/)
      var hours = parseInt(hoursMinutes[0], 10)
      var minutes = hoursMinutes[1] ? parseInt(hoursMinutes[1], 10) : 0
      return (hours + minutes / 60).toFixed(2)
    },
    canReport (projectName) {
      const excludedProjects = ['DC Vacations', 'DC Sick-Leave']
      const isFinancier = this.employeeRoles.financier.val

      if (!isFinancier && excludedProjects.includes(projectName)) {
        this.$store.dispatch('setError', { errorMsg: 'Only Financier role can Save/Update this project report' })
        return false
      }
      return true
    }
  }
}
</script>
<style lang="">

</style>
